"use client";

import clsx from "clsx";
import React, {useEffect, useMemo, useRef} from "react";
import {useTranslation} from "react-i18next";
import FilePreviewContent from "@/components/ask/ask-file-bubble/FilePreviewContent";
import Icon, {ICON_SIZES} from "@/components/library/Icon";
import {type ArtifactData, useArtifactPanel} from "@/contexts/chat/ArtifactPanelContext";
import {useGailDock} from "@/contexts/chat/GailDockContext";
import {useArtifactDownload} from "@/hooks/useArtifactDownload";
import {useBlobUrl, useExcelData} from "@/hooks/useFilePreview";
import {getFileExtension, hasExtensionSuffix} from "@/utils/fileHelpers";
import {detectFileType} from "@/utils/fileTypeDetection";
import ScriptDraftView from "./workspace/ScriptDraftView";

type FileArtifact = Extract<ArtifactData, {kind: "binary" | "generated"}>;

const FileArtifactBody: React.FC<{artifact: FileArtifact}> = ({artifact}) => {
   const isBinary = artifact.kind === "binary";
   const base64 = isBinary ? artifact.base64 : "";
   const mimeType = artifact.mimeType;

   const fileType = useMemo(() => detectFileType(mimeType), [mimeType]);
   const blobUrl = useBlobUrl(isBinary ? base64 : undefined, mimeType);
   const excelData = useExcelData(isBinary ? base64 : undefined, fileType.isExcel);

   const dataUrl = useMemo(
      () => (isBinary ? `data:${mimeType};base64,${base64}` : ""),
      [isBinary, mimeType, base64]
   );

   const url = artifact.kind === "generated" ? artifact.downloadUrl : artifact.url;

   return (
      <FilePreviewContent
         fileType={fileType}
         dataUrl={dataUrl}
         blobUrl={blobUrl}
         excelData={excelData}
         fileName={artifact.fileName}
         mimeType={mimeType}
         url={url}
      />
   );
};

/**
 * Dispatches the panel body by artifact kind. New workspace building blocks
 * plug in here with a new `ArtifactData` variant and a renderer — the panel
 * shell (header, close, layout) stays unchanged.
 */
const ArtifactPanelBody: React.FC<{artifact: ArtifactData}> = ({artifact}) => {
   const {setApprovalPanelHost, setWorkflowPanelHost} = useArtifactPanel();
   if (artifact.kind === "workflows") {
      return <div ref={setWorkflowPanelHost} />;
   }
   if (artifact.kind === "approval") {
      return <div ref={setApprovalPanelHost} className="h-full min-h-0" />;
   }
   if (artifact.kind === "script_draft") {
      // Keyed by artifact id so switching drafts remounts the view: save
      // state, agent name, and direction inputs must not leak across drafts.
      return <ScriptDraftView key={artifact.id} artifactId={artifact.id} draft={artifact.draft} />;
   }
   return <FileArtifactBody artifact={artifact} />;
};

/** Per-kind header metadata: title, panel a11y label, optional badge text, optional download URL. */
const getHeaderMeta = (
   artifact: ArtifactData,
   t: (key: string, options?: Record<string, unknown>) => string
): {title: string; panelLabel: string; badge?: string; downloadHref?: string} => {
   if (artifact.kind === "workflows") {
      return {title: artifact.title, panelLabel: artifact.title};
   }
   if (artifact.kind === "approval") {
      return {title: artifact.title, panelLabel: t("toolApproval.review"), badge: t("toolApproval.title")};
   }
   if (artifact.kind === "script_draft") {
      return {
         title: artifact.draft.scriptName,
         panelLabel: t("workspace.scriptDraft.a11yPanel"),
         badge: t("workspace.scriptDraft.badge"),
      };
   }

   const extension = getFileExtension(artifact.fileName, artifact.mimeType);
   return {
      title: artifact.fileName,
      panelLabel: t("a11y.filePreview"),
      badge: hasExtensionSuffix(artifact.fileName, extension) ? undefined : extension,
      downloadHref: artifact.kind === "generated" ? artifact.downloadUrl : artifact.url,
   };
};

function ArtifactPanelFrame({dialog, panelRef, className, label, children}: {
   dialog: boolean;
   panelRef: React.RefObject<HTMLElement | null>;
   className: string;
   label: string;
   children: React.ReactNode;
}) {
   if (dialog) {
      return <dialog
         ref={panelRef as React.Ref<HTMLDialogElement>}
         open
         tabIndex={-1}
         onCancel={event => event.preventDefault()}
         className={className}
         aria-label={label}
      >{children}</dialog>;
   }
   return <aside ref={panelRef} className={className} aria-label={label}>{children}</aside>;
}

const ArtifactPanel: React.FC = () => {
   const {t} = useTranslation("ask");
   const {activeArtifact, closeArtifact} = useArtifactPanel();
   const {isDockAvailable} = useGailDock();
   const triggerDownload = useArtifactDownload();
   const panelRef = useRef<HTMLElement>(null);
   const approvalId = activeArtifact?.kind === "approval" ? activeArtifact.id : null;

   useEffect(() => {
      if (!approvalId) return;
      const trigger = document.activeElement instanceof HTMLElement ? document.activeElement : null;
      panelRef.current?.focus({preventScroll: true});
      const handleKeyDown = (event: KeyboardEvent) => {
         const panel = panelRef.current;
         if (!panel?.contains(document.activeElement)) return;
         if (event.key === "Escape") {
            event.stopPropagation();
            closeArtifact();
         }
         if (event.key === "Tab" && window.matchMedia("(max-width: 767px)").matches) {
            const controls = Array.from(panel.querySelectorAll<HTMLElement>(
               'button:not(:disabled), textarea:not(:disabled), a[href], [tabindex="0"]'
            )).filter(control => control.getClientRects().length > 0);
            const first = controls[0];
            const last = controls.at(-1);
            if (event.shiftKey && (document.activeElement === first || document.activeElement === panel)) {
               event.preventDefault();
               last?.focus();
            } else if (!event.shiftKey && document.activeElement === last) {
               event.preventDefault();
               first?.focus();
            }
         }
      };
      document.addEventListener("keydown", handleKeyDown);
      return () => {
         document.removeEventListener("keydown", handleKeyDown);
         if (trigger?.isConnected) trigger.focus({preventScroll: true});
      };
   }, [approvalId, closeArtifact]);

   const suppressScriptWorkspace = activeArtifact?.kind === "script_draft" && isDockAvailable;

   useEffect(() => {
      if (suppressScriptWorkspace) closeArtifact();
   }, [suppressScriptWorkspace, closeArtifact]);

   if (!activeArtifact || suppressScriptWorkspace) return null;

   const {title, panelLabel, badge, downloadHref} = getHeaderMeta(activeArtifact, t);

   return (
      <ArtifactPanelFrame
         dialog={!!approvalId}
         panelRef={panelRef}
         label={panelLabel}
         className={clsx(
            "flex flex-col bg-panel border-l border-300 outline-none",
            "h-full w-full",
            // On mobile, overlay the chat; on md+, we live inside a flex row.
            "fixed inset-0 z-40 md:static md:z-auto md:w-auto",
            // The UA dialog stylesheet sets margin, padding, border, and canvas text.
            approvalId && "m-0 max-h-none max-w-none border-0 p-0 text-inherit"
         )}
      >
         <header className="flex items-center gap-3 px-4 py-3 border-b border-300 flex-shrink-0">
            {badge && !approvalId && (
               <span className="px-2 py-0.5 rounded-md text-[10px] font-semibold uppercase tracking-wide bg-200 text-700 flex-shrink-0">
                  {badge}
               </span>
            )}
            <div className="flex-1 min-w-0">
               {approvalId && <p className="mb-1 text-xs font-medium text-500">{badge}</p>}
               <p className={clsx("text-sm font-medium text-900", approvalId ? "break-words" : "truncate")} title={title}>
                  {title}
               </p>
            </div>
            {downloadHref && (
               <button
                  type="button"
                  onClick={() => triggerDownload(downloadHref, title)}
                  aria-label={t("a11y.downloadFile")}
                  title={t("a11y.downloadFile")}
                  className="inline-flex items-center justify-center h-8 w-8 rounded-md text-700 hover:text-900 hover:bg-200 transition-colors"
               >
                  <Icon icon="download" size={ICON_SIZES.SM} />
               </button>
            )}
            <button
               type="button"
               onClick={() => closeArtifact()}
               aria-label={t("a11y.close", {defaultValue: "Close"})}
               title={t("a11y.close", {defaultValue: "Close"})}
               className="inline-flex items-center justify-center h-8 w-8 rounded-md text-700 hover:text-900 hover:bg-200 transition-colors"
            >
               <Icon icon="xmark" size={ICON_SIZES.SM} />
            </button>
         </header>
         <div className="flex-1 overflow-auto bg-100 min-h-0">
            <ArtifactPanelBody key={activeArtifact.id} artifact={activeArtifact} />
         </div>
      </ArtifactPanelFrame>
   );
};

export default ArtifactPanel;
